import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { ProgressSpinner } from 'primereact/progressspinner';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Accordion, AccordionTab } from 'primereact/accordion';
import leadSharingSettingsService from '../../services/leadSharingSettingsService';

/**
 * Company Sharing Settings
 * Tenant Admin manages cross-company lead sharing toggles.
 *
 * - View all tenant companies with their sharing + receiving settings
 * - Toggle cross-company sharing (send leads out)
 * - Toggle cross-company receiving (accept incoming leads)
 * - Debounced server-side search (P13)
 * - Aggregate KPI cards at top (backend should return `stats` in response;
 *   falls back to current-page counts if backend hasn't implemented yet)
 */
const CompanySharingSettings = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState({});
    const [globalFilter, setGlobalFilter] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    // KPI stats (prefer backend aggregates; fallback computed from page)
    const [stats, setStats] = useState({
        total: 0,
        sharing: 0,
        receiving: 0,
        both: 0
    });

    const toast = useRef(null);

    // Debounce search input (P13) — updates debouncedSearch 500ms after user stops typing
    // Also resets pagination to page 1 on new search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(globalFilter.trim());
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    // Fetch when page or debounced search changes (P14 — primitive deps only)
    useEffect(() => {
        loadCompanies();
    }, [pagination.page, debouncedSearch]);

    const loadCompanies = async () => {
        setLoading(true);
        try {
            const result = await leadSharingSettingsService.getCompaniesWithSharingSettings({
                page: pagination.page,
                limit: pagination.limit,
                search: debouncedSearch || undefined
            });

            if (result.success) {
                const list = result.data.companies || [];
                const pag = result.data.pagination || {};
                const backendStats = result.data.stats; // optional — see fallback below

                setCompanies(list);
                setPagination(prev => ({
                    ...prev,
                    total: pag.total || 0,
                    totalPages: pag.total_pages || 0
                }));

                if (backendStats) {
                    // Preferred: backend returns aggregates across ALL companies
                    setStats({
                        total: backendStats.total_companies ?? pag.total ?? 0,
                        sharing: backendStats.sharing_enabled ?? 0,
                        receiving: backendStats.receiving_enabled ?? 0,
                        both: backendStats.both_enabled ?? 0
                    });
                } else {
                    // Fallback: compute from current page (inaccurate for paginated data)
                    // TODO (backend): Return response.data.stats with aggregate counts
                    setStats({
                        total: pag.total || 0,
                        sharing: list.filter(c => c.cross_company_sharing_enabled).length,
                        receiving: list.filter(c => c.cross_company_receiving_enabled).length,
                        both: list.filter(c => c.cross_company_sharing_enabled && c.cross_company_receiving_enabled).length
                    });
                }
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to load companies',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Failed to load companies:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error?.message || 'Failed to load companies',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSharingToggle = async (companyId, field, newValue) => {
        setUpdating(prev => ({ ...prev, [`${companyId}-${field}`]: true }));

        try {
            const settings = { [field]: newValue };
            const result = await leadSharingSettingsService.updateCompanySharingSettings(companyId, settings);

            if (result.success) {
                // Update local state optimistically
                setCompanies(prev => prev.map(company =>
                    company.id === companyId
                        ? { ...company, [field]: newValue }
                        : company
                ));

                // Recompute stats delta locally (avoids full refetch)
                setStats(prev => {
                    const delta = newValue ? 1 : -1;
                    if (field === 'cross_company_sharing_enabled') {
                        return { ...prev, sharing: Math.max(0, prev.sharing + delta) };
                    }
                    if (field === 'cross_company_receiving_enabled') {
                        return { ...prev, receiving: Math.max(0, prev.receiving + delta) };
                    }
                    return prev;
                });

                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: result.message || 'Settings updated successfully',
                    life: 3000
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to update settings',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Failed to update sharing settings:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error?.message || 'Failed to update settings',
                life: 5000
            });
        } finally {
            setUpdating(prev => ({ ...prev, [`${companyId}-${field}`]: false }));
        }
    };

    const handleClearFilters = () => {
        setGlobalFilter('');
    };

    // KPI card helper (P15 pattern — uniform Card with bg-primary-50 icon chip)
    const KpiCard = ({ icon, label, value, description }) => (
        <div className="col-6 lg:col-3">
            <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                <div className="flex justify-content-between align-items-start">
                    <div className="flex flex-column gap-1">
                        <span className="text-600 text-sm font-medium">{label}</span>
                        <span className="text-900 text-xl font-semibold">{value}</span>
                    </div>
                    <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                        <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                    </div>
                </div>
                <span className="text-500 text-xs block mt-2">{description}</span>
            </Card>
        </div>
    );

    // Column templates
    const companyNameTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-building text-500" aria-hidden="true"></i>
            <div className="flex flex-column">
                <span className="font-medium text-900">{rowData.name}</span>
                {rowData.email && <small className="text-500">{rowData.email}</small>}
            </div>
        </div>
    );

    const adminTemplate = (rowData) => {
        if (!rowData.admin) return <span className="text-500">No admin assigned</span>;
        return (
            <div className="flex flex-column">
                <span className="text-900">{rowData.admin.name}</span>
                <small className="text-500">{rowData.admin.email}</small>
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        const severity = rowData.status === 'active' ? 'success'
            : rowData.status === 'inactive' ? 'warning'
            : 'danger';
        return <Tag value={rowData.status} severity={severity} />;
    };

    const usersCountTemplate = (rowData) => (
        <div className="flex align-items-center justify-content-center gap-2">
            <i className="pi pi-users text-primary" aria-hidden="true"></i>
            <span>{rowData.users_count || 0}</span>
        </div>
    );

    const sharingEnabledTemplate = (rowData) => {
        const isUpdating = updating[`${rowData.id}-cross_company_sharing_enabled`];
        return (
            <div className="flex align-items-center justify-content-center gap-2">
                <InputSwitch
                    checked={rowData.cross_company_sharing_enabled}
                    onChange={(e) => handleSharingToggle(rowData.id, 'cross_company_sharing_enabled', e.value)}
                    disabled={isUpdating || rowData.status !== 'active'}
                />
                {isUpdating && <i className="pi pi-spin pi-spinner text-primary" aria-label="Saving"></i>}
            </div>
        );
    };

    const receivingEnabledTemplate = (rowData) => {
        const isUpdating = updating[`${rowData.id}-cross_company_receiving_enabled`];
        return (
            <div className="flex align-items-center justify-content-center gap-2">
                <InputSwitch
                    checked={rowData.cross_company_receiving_enabled}
                    onChange={(e) => handleSharingToggle(rowData.id, 'cross_company_receiving_enabled', e.value)}
                    disabled={isUpdating || rowData.status !== 'active'}
                />
                {isUpdating && <i className="pi pi-spin pi-spinner text-primary" aria-label="Saving"></i>}
            </div>
        );
    };

    if (loading && companies.length === 0) {
        return (
            <div className="flex justify-content-center align-items-center min-h-20rem">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <>
            <Toast ref={toast} position="top-right" />
            {/* Tooltip attached by class; each target element provides its own data-pr-tooltip text */}
            <Tooltip target=".col-header-tooltip" position="top" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-3">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Sharing Settings</h1>
                        <p className="text-600 text-sm m-0">
                            Manage cross-company lead sharing permissions
                        </p>
                    </div>

                    {/* Collapsed explainer — click to expand for context about what cross-company sharing means */}
                    <Accordion className="mb-3">
                        <AccordionTab
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-info-circle text-primary" />
                                    About Cross-Company Lead Sharing
                                </span>
                            }
                        >
                            <p className="text-600 text-sm m-0">
                                Enable companies to share leads with each other. Both{' '}
                                <strong>Can Share</strong> and <strong>Can Receive</strong> must be
                                enabled for two companies to exchange leads.
                            </p>
                        </AccordionTab>
                    </Accordion>

                    {/* KPI cards — 2 per row mobile, 4 per row desktop (P15) */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-building"
                            label="Total Companies"
                            value={stats.total}
                            description="In your tenant"
                        />
                        <KpiCard
                            icon="pi-share-alt"
                            label="Sharing Enabled"
                            value={stats.sharing}
                            description="Can send leads out"
                        />
                        <KpiCard
                            icon="pi-download"
                            label="Receiving Enabled"
                            value={stats.receiving}
                            description="Can accept incoming leads"
                        />
                        <KpiCard
                            icon="pi-check-circle"
                            label="Both Enabled"
                            value={stats.both}
                            description="Exchange-ready"
                        />
                    </div>

                    <Card>
                        {/* Filter bar — 5-slot grid: 1 filter + 3 spacers + Clear|Refresh (P7) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={globalFilter}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            placeholder="Search companies..."
                                            className="w-full p-inputtext-sm"
                                            maxLength={100}
                                            aria-label="Search companies"
                                        />
                                    </IconField>
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={handleClearFilters}
                                        disabled={!globalFilter}
                                        aria-label="Clear search"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        icon="pi pi-refresh"
                                        label="Refresh"
                                        severity="info"
                                        outlined
                                        size="small"
                                        onClick={loadCompanies}
                                        tooltip="Refresh company list"
                                        tooltipOptions={{ position: 'top' }}
                                        aria-label="Refresh company list"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={companies}
                            loading={loading}
                            paginator
                            rows={pagination.limit}
                            totalRecords={pagination.total}
                            lazy
                            first={(pagination.page - 1) * pagination.limit}
                            onPage={(e) => setPagination(prev => ({ ...prev, page: Math.floor(e.first / e.rows) + 1, limit: e.rows }))}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-building pi-2x text-400 mb-3" />
                                    <span className="text-600">No companies found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="name"
                                header="Company"
                                body={companyNameTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '250px' }}
                            />
                            <Column
                                field="admin"
                                header="Admin"
                                body={adminTemplate}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={statusTemplate}
                                sortable
                                style={{ minWidth: '100px' }}
                            />
                            <Column
                                field="users_count"
                                header="Users"
                                body={usersCountTemplate}
                                sortable
                                style={{ minWidth: '90px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                            <Column
                                field="cross_company_sharing_enabled"
                                header={
                                    <span
                                        className="flex align-items-center gap-1 col-header-tooltip"
                                        data-pr-tooltip="Company can send leads to other companies"
                                    >
                                        <i className="pi pi-share-alt" aria-hidden="true"></i>
                                        <span>Can Share</span>
                                    </span>
                                }
                                body={sharingEnabledTemplate}
                                style={{ minWidth: '130px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                            <Column
                                field="cross_company_receiving_enabled"
                                header={
                                    <span
                                        className="flex align-items-center gap-1 col-header-tooltip"
                                        data-pr-tooltip="Company can receive leads from other companies"
                                    >
                                        <i className="pi pi-download" aria-hidden="true"></i>
                                        <span>Can Receive</span>
                                    </span>
                                }
                                body={receivingEnabledTemplate}
                                style={{ minWidth: '140px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default CompanySharingSettings;
